/**
 * @fileoverview 返回首页方法
 * @description 根据不同的环境和参数，提供多种跳转首页的方式
 * @param {Object} params - 跳转参数对象
 * @param {string} [params.url] - 目标URL，如果有则直接跳转
 * @param {string} [params.type] - 环境类型：'wx'表示微信小程序内 redirectTo；'wxback' 表示调用小程序 navigateBack；'tappback' 表示调用 TAPP 返回原生页；其他为普通 web
 * @param {string} [params.json_str] - 需要传递给业务方的JSON字符串，可包含 delta 字段控制返回页面数
 * @param {string} [params.resultCode] - type 为 wxback 或 tappback 时可选，'1' 表示成功，'0' 表示失败
 * @param {string} [params.from] - 来源标识
 * @param {Object} [params.otherParams] - 其他需要传递的参数
 * @param {number} [params.json_str.delta] - type 为 wxback 时可选，从 json_str 中解析，返回的页面数，默认 1
 */

const BAIDU_SWAN_SDK = 'https://b.bdstatic.com/searchbox/icms/searchbox/js/swan-2.0.31.js';
const KUAISHOU_SDK = 'https://mp.kuaishou.com/mp/jssdk/ks-mp-jssdk.js';
const ALIPAY_WEBVIEW_SDK = 'https://appx/web-view.min.js';
const ALIPAY_WEBVIEW_SDK_FALLBACK =
  'https://gw.alipayobjects.com/as/g/h5-lib/alipayjsapi/3.1.1/alipayjsapi.min.js';

const appendScript = (src, onLoad, onError) => {
  const script = document.createElement('script');
  script.src = src;
  script.onload = onLoad;
  script.onerror = onError;
  document.head.appendChild(script);
};

const loadAlipaySDK = (callback) => {
  if (typeof my !== 'undefined') {
    callback();
    return;
  }
  appendScript(
    ALIPAY_WEBVIEW_SDK,
    () => callback(),
    () => {
      appendScript(
        ALIPAY_WEBVIEW_SDK_FALLBACK,
        () => callback(),
        () => console.error('gotoHome: 支付宝 JSSDK 加载失败'),
      );
    },
  );
};

const loadBaiduSDK = (callback) => {
  if (window.swan?.webView) {
    callback();
    return;
  }
  appendScript(
    BAIDU_SWAN_SDK,
    () => callback(),
    () => console.error('gotoHome: 百度 SDK 加载失败'),
  );
};

const loadKsSDK = (callback) => {
  if (typeof ks !== 'undefined') {
    callback();
    return;
  }
  appendScript(
    KUAISHOU_SDK,
    () => callback(),
    () => console.error('gotoHome: 快手 SDK 加载失败'),
  );
};

/** @returns {string} */
const getUserAgent = () => (typeof navigator !== 'undefined' ? navigator.userAgent : '');

/** 百度智能小程序 web-view */
const isBaiduXcxWeb = (ua) => /swan\//i.test(ua) || /^webswan-/.test(window.name || '');

/** 支付宝小程序 web-view */
const isAlipayXcxWeb = (ua) => /AlipayClient/i.test(ua);

/** 快手小程序 web-view */
const isKuaishouXcxWeb = (ua) => /miniProgram/i.test(ua) && /kwai|kwapp/i.test(ua);

const closeBaiduWebView = () => {
  const navigateBack = () => {
    window.swan.webView.navigateBack({
      fail: (err) => console.error('gotoHome: swan.webView.navigateBack 失败', err),
    });
  };

  if (window.swan?.webView) {
    navigateBack();
    return;
  }

  loadBaiduSDK(() => {
    if (!window.swan?.webView) {
      console.error('gotoHome: 百度 SDK 加载后 swan.webView 仍不可用');
      return;
    }
    requestAnimationFrame(navigateBack);
  });
};

const closeAlipayWebView = () => {
  loadAlipaySDK(() => {
    if (typeof my === 'undefined') {
      console.error('gotoHome: 支付宝 JSSDK 加载后 my 仍不可用');
      return;
    }
    my.navigateBack({
      fail: () => console.error('gotoHome: my.navigateBack 失败'),
    });
  });
};

const closeKuaishouWebView = () => {
  loadKsSDK(() => {
    if (typeof ks === 'undefined') {
      console.error('gotoHome: 快手 SDK 加载后 ks 仍不可用');
      return;
    }
    ks.navigateBack({
      fail: () => console.error('gotoHome: ks.navigateBack 失败'),
    });
  });
};

/**
 * closeme：关闭百度 / 支付宝 / 快手小程序当前 web-view
 */
const closeMeWebView = () => {
  const ua = getUserAgent();

  if (isBaiduXcxWeb(ua)) {
    closeBaiduWebView();
    return;
  }
  if (isAlipayXcxWeb(ua)) {
    closeAlipayWebView();
    return;
  }
  if (isKuaishouXcxWeb(ua)) {
    closeKuaishouWebView();
    return;
  }

  console.error('gotoHome: closeme 当前环境不是百度/支付宝/快手小程序 web-view');
};

const gotoHome = (params = {}) => {
  // 参数校验
  if (!params || typeof params !== 'object') {
    console.error('gotoHome: 参数必须是一个对象');
    return;
  }

  const { type, json_str, resultCode, ...otherParams } = params;
  const url = params.redirect_url;
  const from = params.from;

  // 百度 / 支付宝 / 快手：仅关闭当前 web-view
  if (type === 'closeme') {
    closeMeWebView();
    return;
  }

  // 微信小程序：返回上一页或多页
  if (type === 'wxback') {
    if (!window.wx || !window.wx.miniProgram) {
      console.error('gotoHome: 当前环境不支持微信小程序API');
      return;
    }

    // 从 json_str 中解析 delta
    let delta = 1;
    if (json_str) {
      try {
        const parsed = JSON.parse(json_str);
        const rawDelta = parsed.delta != null ? Number(parsed.delta) : 1;
        delta = Number.isFinite(rawDelta) && rawDelta > 0 ? rawDelta : 1;
      } catch (e) {
        console.warn('gotoHome: json_str 解析失败，使用默认 delta', e);
      }
    }

    if (resultCode !== undefined) {
      wx.miniProgram.postMessage({
        data: {
          resultCode
        }
      });
    }

    wx.miniProgram.navigateBack({ delta });
    return;
  }

  // TAPP：通知 Flutter 高风险结果后关闭 WebView
  if (type === 'tappback') {
    if (typeof _tc_bridge_util === 'undefined' || typeof _tc_bridge_web === 'undefined') {
      console.error('gotoHome: 当前环境不支持 TAPP bridge API');
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      _tc_bridge_util.hotel_common_business({
        param: {
          method: 'hotelH5ToFlutterDataCommunication',
          arguments: {
            method: 'submitOrderHighRisk',
            params: { resultCode },
          },
        },
        callback: (data) => {
          resolve(data);
        },
      });
      _tc_bridge_web.close({ param: {} });
    });
  }

  // 如果有url参数，直接处理url跳转
  if (url) {
    try {
      const decodedUrl = decodeURIComponent(url);

      // shieldHotelWx 场景需附带 json_str
      const jsonUrl =
        from === 'shieldHotelWx' && json_str
          ? `${decodedUrl}?json_str=${json_str}&validate=1`
          : decodedUrl;

      // 微信小程序环境处理
      if (type === 'wx') {
        if (!window.wx || !window.wx.miniProgram) {
          console.error('gotoHome: 当前环境不支持微信小程序API');
          return;
        }
        wx.miniProgram.redirectTo({ url: jsonUrl });
      } else if (type === 'tapp') {
        // TAPP环境处理
        _tc_bridge_web.close({ param: {} });
        return;
      } else {
        // 普通web环境处理
        window.location.replace(decodedUrl);
      }
      return;
    } catch (e) {
      console.error('gotoHome: URL解码失败', e);
      return;
    }
  }

  // 没有url参数时的处理
  try {
    // TAPP环境处理
    if (type === 'tapp') {
      _tc_bridge_web.close({ param: {} });
      return;
    }

    // 微信小程序：无跳转地址时关闭当前 web-view
    if (type === 'wx') {
      if (!window.wx || !window.wx.miniProgram) {
        console.error('gotoHome: 当前环境不支持微信小程序API');
        return;
      }
      wx.miniProgram.navigateBack({ delta: 1 });
      return;
    }

    // 默认web处理：构建首页URL并携带参数
    const [baseUrl] = window.location.href.split('#');
    const hash = '#/';

    // 构建参数部分
    const paramsStr = Object.keys(otherParams)
      .filter((key) => otherParams[key] !== undefined)
      .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(otherParams[key])}`)
      .join('&');

    const targetUrl = paramsStr ? `${baseUrl}${hash}?${paramsStr}` : `${baseUrl}${hash}`;

    window.location.replace(targetUrl);
  } catch (e) {
    console.error('gotoHome: 跳转失败', e);
  }
};

export default gotoHome;
